/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
const GA_MEASUREMENT_ID = "G-QD7E74MKXF";

const EVENT_ALL_CLICK = "all_click";
const EVENT_VIEW_LIST = "view_item_list";
const EVENT_SELECT_ITEM = "select_item";
const EVENT_VIEW_PROMO = "view_promotion";
const EVENT_SELECT_PROMO = "select_promotion";

const PRODUCT_ITEM_CLICK_SELECTOR = ".merca_plp_click";
const PRODUCT_ITEM_VIEW_SELECTOR = ".merca_plp_click";
const PROMO_ITEM_VIEW_SELECTOR = ".merca_promo_view";
const PROMO_ITEM_CLICK_SELECTOR = ".merca_promo_click";

const DEBOUNCE_INTERVAL = 1000;

let observedProductItems = [];
let observedPromoItems = [];
let observerParent = null;
let observerPromoParent = null;

/** Utility: Simple debounce */
function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/** Extract standard click data for GA4 all_click event */
function extractClickData(el) {
  return {
    send_to: GA_MEASUREMENT_ID,
    click_element: el?.tagName?.toLowerCase() || null,
    click_id: el?.id || null,
    click_class: el?.className || null,
    click_target: el?.getAttribute?.("target") || null,
    click_text: el?.innerText?.trim().slice(0, 100) || null,
    click_url: el?.getAttribute?.("href") || el?.getAttribute?.("src") || null,
  };
}

/** Check if click data is meaningful */
function isClickDataEmpty(data) {
  return (
    !data.click_id &&
    (!data.click_class || typeof data.click_class === "object") &&
    !data.click_text &&
    !data.click_url
  );
}

/** Extract product metadata from DOM element */
function extractProductData(el) {
  return {
    item_id: el.getAttribute("data-product-id"),
    item_name: el.getAttribute("data-product-name"),
    // item_brand: el.getAttribute("data-product-vendor"),
    item_list_id:
      el.getAttribute("data-collection-id") ||
      el.getAttribute("data-custom-list-id") ||
      "home",
    item_list_name:
      el.getAttribute("data-product-collection") ||
      el.getAttribute("data-custom-list-name") ||
      "home",
    item_variant: el.getAttribute("data-product-variant"),
    index: el.getAttribute("product-index-number"),
    price: el.getAttribute("data-product-price"),
    quantity: 1,
  };
}

/** Send GA4 event */
function sendGAEvent(eventName, params) {
  if (typeof gtag === "function") {
    gtag("event", eventName, {
      send_to: GA_MEASUREMENT_ID,
      ...params,
    });
  }
}

/** IntersectionObserver callback for view_item_list */
function handleProductInView(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const el = entry.target;
      const productData = extractProductData(el);
      observedProductItems.push(productData);

      if (observedProductItems.length >= 2) {
        sendGAEvent(EVENT_VIEW_LIST, {
          item_list_id: productData.item_list_id,
          item_list_name: productData.item_list_name,
          items: observedProductItems,
        });

        observedProductItems = []; // Reset after send
      }

      // Optionally unobserve after first view
      // observerParent.unobserve(el);
    }
  });
}

/** Setup IntersectionObserver for view_item_list */
function setupIntersectionObserver() {
  const observerOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.8,
  };

  observerParent = new IntersectionObserver(
    handleProductInView,
    observerOptions
  );
}

/** Observe any unobserved product items */
function observeNewProductItems() {
  const items = document.querySelectorAll(PRODUCT_ITEM_VIEW_SELECTOR);
  items.forEach((item) => {
    if (!item.dataset.tracked) {
      observerParent.observe(item);
      item.dataset.tracked = "true";
    }
  });
}

/** Setup all_click tracking */
function setupAllClickTracking() {
  document.addEventListener("click", function (event) {
    let element = event.target;
    let clickData = extractClickData(element);

    while (isClickDataEmpty(clickData) && element.parentElement) {
      element = element.parentElement;
      clickData = extractClickData(element);
    }

    sendGAEvent(EVENT_ALL_CLICK, clickData);

    // Debug (optional)
    // console.log("GA4 all_click:", clickData);
  });
}

/** Setup select_item tracking on collection clicks */
function setupCollectionClickTracking() {
  const trackingItems = document.querySelectorAll(PRODUCT_ITEM_CLICK_SELECTOR);

  trackingItems.forEach((item) => {
    if (!item.dataset.boundClick) {
      item.addEventListener("click", function () {
        const productData = extractProductData(this);
        sendGAEvent(EVENT_SELECT_ITEM, {
          item_list_id: productData.item_list_id,
          item_list_name: productData.item_list_name,
          items: [productData],
        });
        // Debug (optional)
        // console.log("GA4 select_item:", productData);
      });
      item.dataset.boundClick = "true";
    }
  });
}

/** Extract promo metadata from DOM element */
function extractPromoData(el) {
  return {
    promotion_id: el.getAttribute("data-promo-id"),
    promotion_name: el.getAttribute("data-promo-name"),
    creative_name: el.getAttribute("data-creative-name"),
    creative_slot: el.getAttribute("promo-index-number"),
  };
}

/** Observe any unobserved promo items */
function observeNewPromoItems() {
  const items = document.querySelectorAll(PROMO_ITEM_VIEW_SELECTOR);
  items.forEach((item) => {
    if (!item.dataset.tracked) {
      observerPromoParent.observe(item);
      item.dataset.tracked = "true";
    }
  });
}

/** IntersectionObserver callback for view_promotion */
function handlePromoInView(entries, observer) {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      const el = entry.target;
      const promoData = extractPromoData(el);

      sendGAEvent(EVENT_VIEW_PROMO, {
        items: promoData,
      });

      // Optionally unobserve after first view
      // observerPromoParent.unobserve(el);
    }
  });
}

/** Setup IntersectionObserver for view_promotion */
function setupPromoIntersectionObserver() {
  const observerOptions = {
    root: null,
    rootMargin: "0px",
    threshold: 0.8,
  };

  observerPromoParent = new IntersectionObserver(
    handlePromoInView,
    observerOptions
  );
}

/** Setup select_item tracking on collection clicks */
function setupPromoClickTracking() {
  const trackingItems = document.querySelectorAll(PROMO_ITEM_CLICK_SELECTOR);

  trackingItems.forEach((item) => {
    if (!item.dataset.boundClick) {
      item.addEventListener("click", function () {
        const promoData = extractPromoData(this);
        sendGAEvent(EVENT_SELECT_PROMO, promoData);
        // Debug (optional)
        // console.log("GA4 select_promotion:", promoData);
      });
      item.dataset.boundClick = "true";
    }
  });
}

/** Observe dynamic changes and update observers/bindings */
function observeDOMChanges() {
  const debouncedUpdate = debounce(() => {
    observeNewProductItems();
    observeNewPromoItems();
    setupCollectionClickTracking();
    setupPromoClickTracking();
  }, DEBOUNCE_INTERVAL);

  const mo = new MutationObserver(debouncedUpdate);
  mo.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/** Initialize tracking */
document.addEventListener("DOMContentLoaded", function () {
  setupIntersectionObserver();
  setupPromoIntersectionObserver();
  observeNewProductItems(); 
  observeNewPromoItems(); 
  setupAllClickTracking();
  setupCollectionClickTracking();   
  setupPromoClickTracking();
  observeDOMChanges(); // for dynamic content
});
